import React, { useEffect, useState } from 'react';
import {
    Container,
    Grid,
    Paper,
    Typography,
    Box,
    Chip,
    Button,
    IconButton,
    Select,
    MenuItem,
    FormControl
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import TitleBar from '../../components/TitleBar/TitleBar';
import {
    getReviewEntityTypes,
    getReviewFrequencies,
    ReviewFrequency
} from '../../services/api';
import {
    AccountTree,
    Business,
    AccountBalance,
    ArrowForward,
    KeyboardArrowDown,
    KeyboardArrowRight,
    CorporateFare
} from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Avatar } from '@mui/material';

interface EntityType {
    id: number;
    category: string;
    name: string;
    display_label: string;
    description: string;
}

interface EntityTypeDisplay {
    type: string;
    icon: React.ReactNode;
}

interface GroupedEntityType {
    category: string;
    entities: EntityType[];
}

const getEntityTypeDisplay = (category: string): EntityTypeDisplay => {
    const displayType = category
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

    const type = category.trim().toUpperCase();
    let icon;

    switch (type) {
        case 'PROJECT':
            icon = <AccountTree sx={{ fontSize: 48, color: 'primary.main' }} />;
            break;
        case 'PROGRAM':
            icon = <Business sx={{ fontSize: 48, color: 'primary.main' }} />;
            break;
        case 'ACCOUNT':
            icon = <AccountBalance sx={{ fontSize: 48, color: 'primary.main' }} />;
            break;
        case 'ORGANIZATION':
            icon = <CorporateFare sx={{ fontSize: 48, color: 'primary.main' }} />;
            break;
        default:
            const firstLetter = category.trim().charAt(0).toUpperCase();
            icon = (
                <Avatar sx={{ width: 48, height: 48, bgcolor: 'primary.main', fontSize: '1.5rem' }}>
                    {firstLetter}
                </Avatar>
            );
    }

    return {
        type: displayType,
        icon: icon
    };
};

const shortenName = (name: string): string => {
    return name.length > 50 ? name.substring(0, 47) + '...' : name;
};

const EntityTypes = () => {
    const [entityTypes, setEntityTypes] = useState<EntityType[]>([]);
    const [error, setError] = useState('');
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
    const [reviewFrequencies, setReviewFrequencies] = useState<{ [key: string]: ReviewFrequency[] }>({});
    const [selectedFrequencies, setSelectedFrequencies] = useState<{ [key: string]: string }>({});
    const navigate = useNavigate();

    useEffect(() => {
        let called = false;
        const fetchEntityTypes = async () => {
            try {
                const response = await getReviewEntityTypes();
                setEntityTypes(response.data);

                if (response.data.length > 0) {
                    setExpandedGroups(new Set([response.data[0].category]));
                }
                


                
                // Fetch review frequencies only once
                let freqResponse;
                try {
                    freqResponse = await getReviewFrequencies();
                } catch (err) {
                    setError('Failed to load review frequencies');
                    console.error('Error fetching review frequencies:', err);
                    return;
                }
                const allFrequencies = freqResponse.data;
                const frequencies: { [key: string]: ReviewFrequency[] } = {};
                const selected: { [key: string]: string } = {};

                for (const entityType of response.data) {
                    frequencies[entityType.name] = allFrequencies;
                    if (allFrequencies.length > 0) {
                        selected[entityType.name] = allFrequencies[0].name;
                    }
                }
                
                setReviewFrequencies(frequencies);
                setSelectedFrequencies(selected);
            } catch (err) {
                setError('Failed to load entity types');
                console.error('Error fetching entity types:', err);
            }
        };

        if (!called) {
            called = true;
            fetchEntityTypes();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleGoToQuestionnaire = (entityType: EntityType) => {
        const selectedFrequency = selectedFrequencies[entityType.name];
        if (!selectedFrequency) {
            console.error('No frequency selected for', entityType.name);
            return;
        }

        navigate(`/questionnaire/${encodeURIComponent(entityType.name)}/${encodeURIComponent(selectedFrequency)}`);
    };

    const toggleGroup = (category: string) => {
        setExpandedGroups(prev => {
            const newSet = new Set(prev);
            if (newSet.has(category)) {
                newSet.delete(category);
            } else {
                newSet.add(category);
            }
            return newSet;
        });
    };

    const groupedEntityTypes: GroupedEntityType[] = Object.entries(
        entityTypes.reduce((acc, entity) => {
            if (!acc[entity.category]) {
                acc[entity.category] = [];
            }
            acc[entity.category].push(entity);
            return acc;
        }, {} as Record<string, EntityType[]>)
    ).map(([category, entities]) => ({
        category,
        entities
    }));

    return (
       <>
        <TitleBar title="" />
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            
           <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 3 }}>
  <IconButton
    aria-label="Back"
    onClick={() => navigate('/home')}
    sx={{
      border: '1px solid #ccc',
      borderRadius: 2,
      p: 1,
      backgroundColor: '#f5f5f5',
      '&:hover': {
        backgroundColor: '#e0e0e0',
      },
      mr: 2,
    }}
  >
    <ArrowBackIcon />
  </IconButton>
  <Typography variant="h4" fontWeight="bold">
    Entity Types
  </Typography>
</Box>


            {error && (
                <Typography color="error" sx={{ mb: 2 }}>
                    {error}
                </Typography>
            )}

            <Grid container spacing={3}>
                {groupedEntityTypes.map(group => (
                    <Grid item xs={12} key={group.category}>
                        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    mb: 2
                                }}
                                onClick={() => toggleGroup(group.category)}
                            >
                                <IconButton size="small">
                                    {expandedGroups.has(group.category) ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                                </IconButton>
                                <Typography variant="h6" component="div">
                                    {group.category}
                                </Typography>
                            </Box>

                            {expandedGroups.has(group.category) && (
                                <Grid container spacing={3}>
                                    {group.entities.map(entityType => {
                                        const display = getEntityTypeDisplay(entityType.category);
                                        return (
                                            <Grid item xs={12} sm={6} md={4} key={entityType.id}>
                                                <Paper
                                                    sx={{
                                                        p: 2,
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        minHeight: '200px',
                                                        bgcolor: 'grey.50',
                                                        '&:hover': {
                                                            bgcolor: 'grey.100'
                                                        }
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                                                        {display.icon}
                                                        <Box sx={{ ml: 2, overflow: 'hidden' }}>
                                                            <Typography
                                                                variant="subtitle1"
                                                                sx={{
                                                                    fontWeight: 'bold',
                                                                    display: '-webkit-box',
                                                                    WebkitLineClamp: 2,
                                                                    WebkitBoxOrient: 'vertical',
                                                                    overflow: 'hidden'
                                                                }}
                                                            >
                                                                {shortenName(entityType.display_label)}
                                                            </Typography>
                                                            <Chip label={display.type} size="small" sx={{ mt: 0.5 }} />
                                                        </Box>
                                                    </Box>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{
                                                            mb: 'auto',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden'
                                                        }}
                                                    >
                                                        {entityType.description}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                                        <FormControl sx={{ minWidth: 120, flex: 1 }}>
                                                            <Select
                                                                size="small"
                                                                value={selectedFrequencies[entityType.name] || ''}
                                                                onChange={e =>
                                                                    setSelectedFrequencies(prev => ({
                                                                        ...prev,
                                                                        [entityType.name]: e.target.value
                                                                    }))
                                                                }
                                                            >
                                                                {reviewFrequencies[entityType.name]?.map(freq => (
                                                                    <MenuItem key={freq.id} value={freq.name}>
                                                                        {freq.display_label}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                        <Button
                                                            variant="contained"
                                                            endIcon={<ArrowForward />}
                                                            onClick={() => handleGoToQuestionnaire(entityType)}
                                                        >
                                                            Show
                                                        </Button>
                                                    </Box>
                                                </Paper>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            )}
                        </Paper>
                    </Grid>
                ))}
            </Grid>
        </Container>
        </>
    );
};

export default EntityTypes;
